import React, { useState, useEffect, useMemo } from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import { format } from 'date-fns';
import RenderDays from './renderDays';
import { getEthiopianMonthsInGregorianMonth, groupEthiopianMonthsByYear, toGeezNumber } from '../../../../util/ethiopia/ethiopianCalendar';
import { useCalendar } from '../calendarContext';
import RenderEventListForMonth from './rederEventListForMonth';
import { colorNameToHex } from '../../../../util/colorMap';


const StyledPaper = ({ children }) => (
  <Paper
    style={{ 
      overflow: 'hidden', // Prevent overflow that causes scroll
    }}
  >
    {children}
  </Paper>
);

const MonthView = ({ monthDate, showEventDetailsInside, showEventDetailsOutside, calculateHeight, printPreviewHeight }) => {
  const { bookingDatas } = useCalendar();
  const [mHeight, setMHeight] = useState(null);

  const updateHeight = useMemo(() => {
    if (!calculateHeight) return null;

    const dpi = 88; // standard screen DPI (96)
    let heightPx = window.innerHeight; // default fallback

    if (typeof printPreviewHeight === 'string') {
      const match = printPreviewHeight.trim().match(/^([0-9.]+)(in|cm)$/i);
      if (match) {
        const value = parseFloat(match[1]);
        const unit = match[2].toLowerCase();

        if (value === 0) {
          heightPx = window.innerHeight;
        } else if (unit === 'in') {
          heightPx = value * dpi;
        } else if (unit === 'cm') {
          heightPx = value * (dpi / 2.54);
        }
      }
    } else if (typeof printPreviewHeight === 'number' && printPreviewHeight > 0) {
      heightPx = printPreviewHeight;
    }

    return heightPx / 6;
  }, [calculateHeight, printPreviewHeight]); 

  useEffect(() => {
    if (updateHeight !== null) {
      setMHeight(updateHeight);
    }
  }, [updateHeight]);

  if (!monthDate) return null; 

  const { monthsWithYears = [] } = getEthiopianMonthsInGregorianMonth(monthDate);

  const grouped = groupEthiopianMonthsByYear(monthsWithYears);
  
  const monthDisplay = Object.entries(grouped)
    .map(([year, months]) => `${months.join(' - ')} ${year} | ${toGeezNumber(Number(year))}`)
    .join(' / ');

  return (
    <Grid
      container 
    >
      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
        <StyledPaper>
          {/* Header */}
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 2, py: 2 }}
          >
            {/* Gregorian Header */}
            <Grid item>
              <Typography
                sx={{
                  fontSize: '3rem',
                  fontWeight: 600,
                  fontFamily: '"Inter", "Noto Sans", "Segoe UI", "Roboto", sans-serif',
                  color: colorNameToHex.DarkBlue,
                }}
              >
                {format(monthDate, 'MMMM yyyy')}
              </Typography>
            </Grid>

            {/* Ethiopian or Other Calendar Header */}
            <Grid item>
              <Typography
                sx={{
                  fontSize: '3rem',
                  fontWeight: 600,
                  fontFamily: '"Inter", "Noto Sans", "Segoe UI", "Roboto", sans-serif',
                  color: colorNameToHex.DarkBlue,
                }}
              >
                {monthDisplay}
              </Typography>
            </Grid>
          </Grid>


          {/* Calendar Days */}
          <Grid container sx={{ width: '100%' }}>
            {RenderDays && RenderDays({ month: monthDate, bookingDatas, showEventDetailsInside, mHeight })}
          </Grid>

          {/* Events Outside Calendar */}
          {showEventDetailsOutside && RenderEventListForMonth && (
            <div>{RenderEventListForMonth(monthDate, bookingDatas)}</div>
          )}
        </StyledPaper>
      </Grid>
    </Grid>
  );
};

export default MonthView;
